import { useDocumentsRepository } from '@Hooks/useRepository';
import { type ApiBlobDataset, type ApiPrintOptionPayload } from '@Model';
import { type BlobInput } from '@oicana/browser';
import { PrintOptionsRepository } from '@Repositories/Documents';
import { isNullOrEmpty, isOk, useToasts } from '@synqo/frontend-component-library';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { useLoadFile } from './useLoadFile';

interface DocumentDatasets {
    jsonDatasets: Map<string, string> | undefined;
    blobDatasets: Map<string, BlobInput> | undefined;
    hasErrors: boolean;
}

async function resolveAllBlobs(
    blobQueryData: Record<string, ApiBlobDataset>,
    loadBlob: (file_id: string) => Promise<ArrayBuffer | undefined>,
) {
    const records = Object.entries(blobQueryData);

    const blobsWithMetadata = await Promise.all(
        records.map(async ([key, dataset]) => {
            const arrayBuffer = await loadBlob(dataset.file_id);
            if (arrayBuffer === undefined) {
                return undefined;
            }
            const blobDataset: BlobInput = {
                data: new Uint8Array(arrayBuffer),
                metadata: {},
            };
            return [key, blobDataset] as const;
        }),
    );

    const nonNullBlobs = blobsWithMetadata.filter((b) => b !== undefined);

    return new Map<string, BlobInput>(nonNullBlobs);
}

export const useDocumentDatasets = (printOptionId: string, primaryId: string, enabled = true): DocumentDatasets => {
    const printOptions = useDocumentsRepository(PrintOptionsRepository);
    const { loadFile } = useLoadFile();
    const { pushAllToasts } = useToasts();

    const [blobDatasets, setBlobDatasets] = useState<Map<string, BlobInput>>();

    const baseQueryKey = ['print_options', printOptionId, 'datasets', primaryId];
    const queriesEnabled = enabled && !isNullOrEmpty(printOptionId) && !isNullOrEmpty(primaryId);

    const payload: ApiPrintOptionPayload = {
        primary_id: primaryId,
    };

    const [jsonHasErrors, setJsonHasErrors] = useState<boolean>(false);
    const [blobHasErrors, setBlobHasErrors] = useState<boolean>(false);

    const { data: jsonResponse } = useQuery({
        queryFn: async () => {
            const response = await printOptions.getJsonDatasets(printOptionId, payload);

            pushAllToasts(response.messages);
            setJsonHasErrors(!isOk(response));

            return response;
        },
        gcTime: 512,
        enabled: queriesEnabled,
        queryKey: [...baseQueryKey, 'json'],
    });

    const { data: blobResponse } = useQuery({
        queryFn: async () => {
            setBlobHasErrors(false);

            const response = await printOptions.getBlobDatasets(printOptionId);

            pushAllToasts(response.messages);
            setBlobHasErrors(!isOk(response));

            return response;
        },
        enabled: queriesEnabled,
        queryKey: [...baseQueryKey, 'blob'],
    });

    const jsonQueryData = jsonResponse?.data;
    const blobQueryData = blobResponse?.data;

    const jsonDatasets = useMemo(
        () => (jsonQueryData === undefined ? undefined : new Map(Object.entries(jsonQueryData))),
        [jsonQueryData],
    );

    useEffect(() => {
        if (blobQueryData === undefined) {
            setBlobDatasets(undefined);
            return;
        }
        resolveAllBlobs(blobQueryData, loadFile).then(setBlobDatasets);
    }, [blobQueryData, loadFile, setBlobDatasets]);

    return {
        jsonDatasets,
        blobDatasets,
        hasErrors: jsonHasErrors || blobHasErrors,
    };
};
